// What one real order would get: which templates switched on and why, the
// order's checks, then each stop in order with who is asked. Assembled by the
// server (POST tripCheckpointTemplates/preview) with the template being edited
// standing in for its saved copy — the same assembly the copy sent to a driver
// will come from. Not the order popup — that comes later.
//
// Office | Driver | Sub switches between that list and a picture of what the
// driver or the subcontractor gets on the phone (PhonePreview).
import type { TripCheckpointAssembledItem, TripCheckpointPreview } from '@my/tms-api';
import { ErrorBanner, cn } from '@my/ui';
import { Check, Loader2, Lock, Minus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { TaskTypeBadge } from '@/features/orders/components/badges';
import { adaptTaskType } from '@/features/orders/lib/adapters';
import { taskTypeLabel } from '@/features/orders/lib/taskTypeConfig';
import {
  DUE_LABEL,
  KINDS,
  MOMENT_LABEL,
  PROPERTY_LABEL,
  ROLES,
  type CargoProperty,
  type OnSiteRole,
} from '../lib/model';
import { checkpointFromWire, roleFromWire } from '../lib/wire';
import { PhonePreview } from './PhonePreview';
import { RoleTag } from './roles';

type View = 'office' | OnSiteRole;

const VIEWS: { id: View; label: string }[] = [
  { id: 'office', label: 'Office' },
  { id: 'driver', label: 'Driver' },
  { id: 'sub', label: 'Sub' },
];

export interface PreviewOrderOption {
  id: string;
  label: string;
}

/** Names from Directory for what the preview carries as ids. */
export interface PreviewNames {
  customer: string | null;
  /** Address id → "Kilkenny DC, Kilkenny". */
  places: ReadonlyMap<string, string>;
}

export function OrderPreview({
  orders,
  orderId,
  onPick,
  search,
  onSearch,
  preview,
  names,
  loading,
  error,
  className,
}: {
  orders: PreviewOrderOption[];
  orderId: string | null;
  onPick: (id: string) => void;
  search: string;
  onSearch: (text: string) => void;
  preview: TripCheckpointPreview | undefined;
  names: PreviewNames;
  loading: boolean;
  error: Error | null;
  className?: string;
}) {
  const [view, setView] = useState<View>('office');
  const orderLabel = orders.find((o) => o.id === orderId)?.label ?? '';

  return (
    <aside className={cn('flex flex-col rounded-lg border border-slate-200 bg-white', className)}>
      <header className="space-y-2 border-b border-slate-200 px-4 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Preview on an order
            {loading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" aria-label="Updating" />}
          </h3>
          <div role="tablist" aria-label="Seen by" className="inline-flex overflow-hidden rounded-md border border-slate-200">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={view === v.id}
                onClick={() => setView(v.id)}
                className={cn(
                  'px-2 py-0.5 text-[11px]',
                  view === v.id ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-1.5">
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Find an order…"
            aria-label="Find an order"
            className="h-7 w-28 min-w-0 rounded border border-slate-200 px-1.5 text-xs text-slate-700 focus:border-sky-300 focus:outline-none"
          />
          <select
            value={orderId ?? ''}
            onChange={(e) => onPick(e.target.value)}
            aria-label="Order"
            className="h-7 min-w-0 flex-1 rounded border border-slate-200 px-1.5 text-xs text-slate-700"
          >
            {orders.length === 0 && <option value="">No orders found</option>}
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {preview && (
          <>
            <div className="flex flex-wrap items-center gap-1 text-[11px]">
              {names.customer && <span className="font-medium text-slate-800">{names.customer} ·</span>}
              <span className="font-medium text-slate-800">{preview.facts.cargoDefinitionName}</span>
              {preview.facts.serviceTagNames.map((t) => (
                <span key={t} className="rounded-full bg-blue-50 px-1.5 font-mono text-[10px] text-blue-700">
                  {t}
                </span>
              ))}
              {preview.facts.cargoProperties.map((p) => (
                <span key={p} className="rounded-full bg-slate-100 px-1.5 text-[10px] text-slate-600">
                  {PROPERTY_LABEL[`${p[0]!.toLowerCase()}${p.slice(1)}` as CargoProperty]}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
              {ROLES.map((r) => (
                <span key={r} className="flex items-center gap-1">
                  <RoleTag role={r} /> {preview.asked[r]} to do
                </span>
              ))}
            </div>
          </>
        )}
        <p className="text-[10px] text-slate-400">
          Required: the planner gets a warning if it isn't done; the driver or sub must do it in the app. A failed
          check only warns.
        </p>
      </header>

      {error && (
        <div className="p-3">
          <ErrorBanner title="Couldn't build the preview" message={error.message} />
        </div>
      )}

      {!preview && !error && (
        <p className="px-4 py-6 text-center text-xs text-slate-400">
          {orderId ? 'Building the preview…' : 'Pick an order to see what it would get.'}
        </p>
      )}

      {preview && view !== 'office' && (
        <div className={cn('flex-1', loading && 'opacity-60')}>
          <PhonePreview preview={preview} role={view} orderLabel={orderLabel} names={names} />
        </div>
      )}

      {preview && view === 'office' && (
        <div className={cn('flex-1 divide-y divide-slate-100', loading && 'opacity-60')}>
          <Block title="Templates">
            <ul className="space-y-1">
              {preview.matches.map((m) => (
                <li
                  key={m.templateId ?? m.templateName}
                  className={cn('flex items-start gap-1.5 text-xs', !m.matched && 'text-slate-400')}
                >
                  {m.matched ? (
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  ) : (
                    <Minus className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  )}
                  <span className="min-w-0">
                    <span className={m.matched ? 'font-medium text-slate-800' : ''}>{m.templateName}</span>
                    <span className="text-[10px] text-slate-400">
                      {' — '}
                      {!m.matched
                        ? m.reason
                        : m.contributes
                          ? `${m.contributes} checkpoint${m.contributes === 1 ? '' : 's'}`
                          : 'matches, but nothing for these stops'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </Block>

          <Block title="Order" subtitle="Once — due before Open, In progress, Completed or invoice approval">
            <ItemList
              items={preview.order}
              timing={(c) => DUE_LABEL[checkpointFromWire(c).due]}
              empty="No order checkpoints."
            />
          </Block>

          {preview.tasks.map((task) => (
            <Block
              key={task.orderTaskId}
              title={
                <span className="flex min-w-0 items-center gap-2">
                  <TaskTypeBadge type={adaptTaskType(task.taskType)} label={taskTypeLabel(task.taskType)} />
                  <span className="truncate text-slate-700">
                    {(task.addressId && names.places.get(task.addressId)) || `Stop ${task.sequence}`}
                  </span>
                </span>
              }
              subtitle={
                task.doneBy === 'Driver'
                  ? `Our driver${task.who ? ` · ${task.who}` : ''}`
                  : task.doneBy === 'Sub'
                    ? `Subcontracted${task.who ? ` · ${task.who}` : ''}`
                    : 'Not assigned yet — whoever does it'
              }
            >
              <Bookend>Arrived</Bookend>
              <ItemList
                items={task.items}
                timing={(c) => MOMENT_LABEL[checkpointFromWire(c).moment]}
                empty="Nothing to check at this stop."
              />
              <Bookend>Departed</Bookend>
            </Block>
          ))}
        </div>
      )}
    </aside>
  );
}

function Block({ title, subtitle, children }: { title: ReactNode; subtitle?: string; children: ReactNode }) {
  return (
    <section className="px-4 py-2.5">
      <div className="mb-1">
        <div className="text-xs font-semibold text-slate-700">{title}</div>
        {subtitle && <p className="text-[10px] text-slate-400">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

/** Arrival and departure — recorded by the app at every stop, not configured. */
function Bookend({ children }: { children: ReactNode }) {
  return <p className="text-[10px] uppercase tracking-wide text-slate-400">{children}</p>;
}

function ItemList({
  items,
  timing,
  empty,
}: {
  items: TripCheckpointAssembledItem[];
  timing: (checkpoint: TripCheckpointAssembledItem['checkpoint']) => string;
  empty: string;
}) {
  if (items.length === 0) return <p className="py-1 text-[11px] text-slate-400">{empty}</p>;

  return (
    <ul className="my-1 space-y-1.5 border-l border-slate-200 pl-2.5">
      {items.map((item) => {
        const c = item.checkpoint;
        const kind = checkpointFromWire(c).kind;
        const skipped = item.skipped !== null;
        return (
          <li key={item.key} className={cn('flex items-start gap-2', skipped && 'opacity-50')}>
            <span className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm border border-slate-300 bg-white" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className={cn('text-xs text-slate-800', skipped && 'line-through')}>
                {c.label || 'Untitled checkpoint'}
                {!c.required && <span className="text-slate-400"> · optional</span>}
              </p>
              <p className="text-[10px] text-slate-400">
                {skipped ? item.skipped : `${KINDS[kind].label} · ${timing(c)} · ${item.sources.join(' + ')}`}
              </p>
            </div>
            <span className="flex shrink-0 items-center gap-0.5">
              {!skipped && item.askedOf.map((r) => <RoleTag key={r} role={roleFromWire(r)} />)}
              {!c.waivable && (
                <span title="Cannot be waived" className="text-slate-400">
                  <Lock className="h-3 w-3" />
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
